import { z } from "zod";
import { optionalText, reasonSchema, shortTextSchema, ulidSchema } from "./primitives.js";

/**
 * Adding a judge to the community roster.
 *
 * A name and nothing else. No email, no invitation, no account setup — the target is a full panel
 * of five in well under a minute, with the assigning staff member standing at the drum (D15,
 * ux.md §3.5).
 */
export const createJudgeSchema = z.object({
  displayName: shortTextSchema,
  notes: optionalText(shortTextSchema),
  /** Only if the community happens to have it. Never required, never asked for twice. */
  email: optionalText(z.email()),
});

export const assignJudgeSchema = z
  .object({
    userId: ulidSchema.optional(),
    /** Supplying a name instead of an id creates the roster entry and the assignment together,
     *  which is what makes "add judge" one action rather than two screens. */
    newJudge: createJudgeSchema.optional(),
    sessionId: ulidSchema,
    competitionId: ulidSchema,
    categoryId: ulidSchema.optional(),
    phaseId: ulidSchema.optional(),
    /** Issue an access code for this assignment. Off when the event uses invitations instead. */
    issueAccessCode: z.boolean().default(true),
  })
  .refine((value) => value.userId != null || value.newJudge != null, {
    message: "Pick someone from the roster or add a new judge",
    path: ["userId"],
  });

/** "Mostly the same five" is common even when it is not guaranteed (ux.md §3.5). */
export const copyPanelSchema = z.object({
  fromSessionId: ulidSchema,
  toSessionId: ulidSchema,
  competitionId: ulidSchema,
  categoryId: ulidSchema.optional(),
  issueAccessCodes: z.boolean().default(true),
});

export const revokeAssignmentSchema = z.object({
  reason: optionalText(reasonSchema),
});

/** Sign-in for a judge: the code and nothing else. */
export const judgeSignInSchema = z.object({
  accessCode: z.string().trim().min(4).max(10).regex(/^\d+$/, "Access codes are numbers"),
  /** Binds the code to this device. A later presentation from a different device is rejected. */
  deviceId: z.string().min(8).max(100),
});

/**
 * A score.
 *
 * The id is client-generated so a retried submission upserts rather than duplicating, and
 * `version` is the optimistic-concurrency check: a submission carrying a stale version is a
 * conflict the judge is asked about, never a silent overwrite.
 *
 * `total` is what the judge's device computed. The server recomputes from `criteria` and treats a
 * mismatch as a bug worth logging — the judge's running total and the authoritative result come
 * from the same engine (D9), so they should never differ.
 */
export const submitScoreSchema = z.object({
  id: ulidSchema,
  judgeAssignmentId: ulidSchema,
  registrationEntryId: ulidSchema,
  phaseId: ulidSchema.optional(),
  criteria: z.array(z.object({ criterionId: ulidSchema, value: z.number().int() })).min(1),
  total: z.number().int().optional(),
  status: z.enum(["draft", "submitted"]).default("submitted"),
  version: z.number().int().min(1).default(1),
  deviceId: optionalText(z.string().max(100)),
  /** When the judge entered it, from their device — which may be well before it reaches us. */
  recordedAt: z.number().int(),
});

/** A batch from a device that has been offline. Each score is applied independently. */
export const submitScoreBatchSchema = z.object({
  scores: z.array(submitScoreSchema).min(1).max(200),
});

/**
 * Abstention is a first-class action with a reason, not a blank score — "this judge is related to
 * this dancer" and "this judge forgot" must not look the same to the tabulator (ux.md §3.6).
 */
export const abstainSchema = z.object({
  judgeAssignmentId: ulidSchema,
  registrationEntryId: ulidSchema,
  phaseId: ulidSchema.optional(),
  reason: reasonSchema,
});

export const overrideScoreSchema = z.object({
  criteria: z.array(z.object({ criterionId: ulidSchema, value: z.number().int() })).min(1),
  reason: reasonSchema,
});

export const setRunningOrderSchema = z.object({
  categoryId: ulidSchema,
  phaseId: ulidSchema.optional(),
  entryIds: z.array(ulidSchema).min(1),
});

export type SubmitScoreInput = z.infer<typeof submitScoreSchema>;
export type AssignJudgeInput = z.infer<typeof assignJudgeSchema>;
